import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Burly} from 'kb-burly';
import {TypedPaginationResponse} from '../../../responses/pagination.response';
import {ChangelogItem} from '../models/changelog-item.model';
import {forkJoin, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {TorchBadgeUpdatedListener} from '../../../../app/shared/services/torch-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class TorchChangelogService {

  private badgeUpdatedListeners: TorchBadgeUpdatedListener[] = [];

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  getChangelogItems(pageSize = 75, currentPage = 0) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .addQuery('page', (currentPage + 1))
      .addQuery('limit', pageSize)
      .get;

    return this.httpClient.get<TypedPaginationResponse<ChangelogItem>>(endpointURL);
  }

  getChangelog(version: string): Observable<ChangelogItem[]> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .addQuery('filter', `version||$eq||${version}`)
      .get;

    return this.httpClient.get<TypedPaginationResponse<ChangelogItem>>(endpointURL).pipe(
      map(response => response.data)
    );
  }

  getLatestVersion(): Observable<ChangelogItem> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .addSegment('/latest')
      .get;

    return this.httpClient.get<ChangelogItem>(endpointURL);
  }

  updateChangelogItems(items: ChangelogItem[]) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .addSegment('/update')
      .get;

    return this.httpClient.post<ChangelogItem[]>(endpointURL, items).pipe(
      tap(() => this.updateSidebarBadge())
    );
  }

  deleteChangelogItem(item: ChangelogItem) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .addSegment('/' + item.id)
      .get;

    return this.httpClient.delete(endpointURL).pipe(
      tap(() => this.updateSidebarBadge())
    );
  }

  deleteChangelogItems(items: ChangelogItem[]) {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/documentation')
      .addSegment('/changelog')
      .get;

    return forkJoin(items.map(item => {
      return this.httpClient.delete(`${endpointURL}/${item.id}`);
    })).pipe(
      tap(() => this.updateSidebarBadge())
    );
  }

  public addBadgeUpdatedListener(listener: TorchBadgeUpdatedListener) {
    if (!this.badgeUpdatedListeners.includes(listener)) {
      this.badgeUpdatedListeners.push(listener);
    }
  }

  private updateSidebarBadge() {
    this.getLatestVersion().subscribe(latestVersion => {
      this.badgeUpdatedListeners.forEach(listener => {
        listener.badgeUpdated('changelog', latestVersion.version, false);
      });
    });
  }
}
